/* eslint-disable react/prop-types */

// 📦 Importaciones necesarias
import React, { useRef } from 'react';
import { Box, Chip } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  scales,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletToolbar,
} from 'components';
import { useIntl } from 'react-intl';
import { Download, ShareButton } from 'components/Download';

// ✅ Registrar los componentes de Chart.js necesarios
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  scales
);

/**
 * 📊 TrendDemandChart - Componente de gráfico de barras que muestra el demanda por mes.
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.data - Datos de demanda organizados por mes.
 * @param {number} props.trend - Cantidad de meses a mostrar en el gráfico.
 * @returns {JSX.Element} Gráfico de barras renderizado.
 */
export const TrendDemandChart = ({ data, trend }) => {
  const intl = useIntl();
  const chartRef = useRef();
  /**
   * 🔄 Extrae valores de demanda y etiquetas de los datos recibidos.
   * @param {Object} data - Datos en formato { mes: { demanda: valor } }
   * @returns {{ values: number[], labels: string[] }} Valores y etiquetas procesadas.
   */
  function getValues(data) {
    let values = []; // 📉 Lista de valores de demanda
    let labels = []; // 🏷️ Lista de etiquetas (meses)

    for (let key in data) {
      values.push(Number(data[key].demand)); // 📌 Convertir la demanda a número y agregarlo
      labels.push(convertDateFormat(key)); // 📌 Agregar el mes como etiqueta
    }

    return { values, labels };
  }

  /**
   * 🔄 Convierte una fecha en formato 'YYYYMM' a 'Mes Año' (Ejemplo: 202411 -> Nov 25)
   * @param {string} date - Fecha en formato 'YYYYMM'.
   * @returns {string} - Fecha convertida al formato 'Mes Año'.
   */
  function convertDateFormat(date) {
    const year = date.slice(0, 4); // Extrae el año (primeros 4 caracteres)
    const month = date.slice(4, 6); // Extrae el mes (últimos 2 caracteres)

    /**
     * Lista de los nombres de los meses, traducidos según el idioma actual.
     */
    const months = [
      intl.formatMessage({ id: 'app.monthsShort.jan' }),
      intl.formatMessage({ id: 'app.monthsShort.feb' }),
      intl.formatMessage({ id: 'app.monthsShort.mar' }),
      intl.formatMessage({ id: 'app.monthsShort.apr' }),
      intl.formatMessage({ id: 'app.monthsShort.may' }),
      intl.formatMessage({ id: 'app.monthsShort.jun' }),
      intl.formatMessage({ id: 'app.monthsShort.jul' }),
      intl.formatMessage({ id: 'app.monthsShort.aug' }),
      intl.formatMessage({ id: 'app.monthsShort.sep' }),
      intl.formatMessage({ id: 'app.monthsShort.oct' }),
      intl.formatMessage({ id: 'app.monthsShort.nov' }),
      intl.formatMessage({ id: 'app.monthsShort.dec' }),
    ];

    const monthName = months[parseInt(month, 10) - 1]; // Obtener el nombre del mes
    return `${monthName} ${year.slice(2, 4)}`;
  }

  // 📊 Obtener datos procesados
  const { values, labels } = getValues(data);

  // ⚙️ Opciones de configuración del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top', // 🔝 Posición de la leyenda
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true, // Comenzar desde cero en el eje Y
          },
        },
      ],
    },
  };

  // 📈 Datos estructurados para el gráfico
  const dataChart = {
    labels: labels.slice(-trend), // 🏷️ Etiquetas de los meses
    datasets: [
      {
        label: `${intl.formatMessage({ id: 'app.deepview.demand.chart.demandSerie.label' })} (kW)`,
        data: values.slice(-trend), // 📊 Datos de demanda
        backgroundColor: '#ebc057', // 🎨 Color de las barras
        borderRadius: 10, // Redondeo de esquinas
      },
    ],
  };

  return (
    <Portlet>
      <PortletHeader noDivider={true}>
        <Chip
          label={intl.formatMessage({
            id: 'app.deepview.demand.chart.demandSerie.label',
          })}
          sx={{
            bgcolor: 'rgb(165, 54, 147)',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
        <PortletToolbar style={{ display: 'flex', gap: 8 }}>
          <Download
            itemRef={chartRef}
            name={intl.formatMessage(
              {
                id: 'app.chargeTrend.board.historicDashboard.demand.monthTitles',
              },
              { month: trend }
            )}
          />
          <ShareButton
            itemRef={chartRef}
            name={intl.formatMessage(
              {
                id: 'app.chargeTrend.board.historicDashboard.demand.monthTitles',
              },
              { month: trend }
            )}
          />
        </PortletToolbar>
      </PortletHeader>
      <PortletContent>
        <Box height={450} position="relative" ref={chartRef} py={2}>
          {/* 📊 Renderizar gráfico */}
          <Bar data={dataChart} options={options} />
        </Box>
      </PortletContent>
    </Portlet>
  );
};
